// useKeyboardOpen — true while the soft keyboard is visible. VIN-2026-73.
//
// On native iOS this uses the Capacitor Keyboard plugin's willShow/willHide
// events (the reliable signal — useVisualKeyboardOffset's visualViewport math
// reads 0 on some WKWebView surfaces, see Sommelier #158). On web it falls
// back to a focus heuristic: an editable element being focused ≈ keyboard up.
//
// Used to collapse the fixed bottom nav while typing so form screens (Edit
// Wine, Add Wine, …) reclaim the vertical space the nav would otherwise eat.

import { useState, useEffect } from 'react'

function isNativePlatform() {
  return typeof window !== 'undefined'
    && typeof window.Capacitor?.isNativePlatform === 'function'
    && window.Capacitor.isNativePlatform()
}

export function useKeyboardOpen() {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    // ── Native: Capacitor Keyboard plugin events ──────────────────────────
    if (isNativePlatform()) {
      const Keyboard = window.Capacitor?.Plugins?.Keyboard
      if (Keyboard?.addListener) {
        const handles = []
        // addListener returns Promise<PluginListenerHandle> in Capacitor 8;
        // Promise.resolve wraps both promise + direct-handle shapes so cleanup
        // works either way.
        Promise.resolve(Keyboard.addListener('keyboardWillShow', () => setOpen(true)))
          .then(h => handles.push(h)).catch(() => {})
        Promise.resolve(Keyboard.addListener('keyboardWillHide', () => setOpen(false)))
          .then(h => handles.push(h)).catch(() => {})
        return () => { handles.forEach(h => { try { h?.remove?.() } catch { /* noop */ } }) }
      }
      // Plugin missing on native — fall through to the focus heuristic.
    }

    // ── Web / fallback: focus heuristic ───────────────────────────────────
    const isEditable = (el) => {
      if (!el) return false
      const tag = el.tagName
      return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable === true
    }
    const onFocusIn  = () => setOpen(isEditable(document.activeElement))
    // Defer the blur read a tick: focus may be moving between two fields, in
    // which case activeElement is briefly null/body before the next focus.
    const onFocusOut = () => setTimeout(() => setOpen(isEditable(document.activeElement)), 60)
    document.addEventListener('focusin', onFocusIn)
    document.addEventListener('focusout', onFocusOut)
    return () => {
      document.removeEventListener('focusin', onFocusIn)
      document.removeEventListener('focusout', onFocusOut)
    }
  }, [])

  return open
}
